import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axiosInstance from "../../api/axiosInstance";
import { baseurImage } from "../../Baseurl";
import logo1 from "../../assests/logo.png";

// ─── Theme ───────────────────────────────────────────────────────────────────

const COLORS = {
  gray:   '#5B6B79',
  blue:   '#96C5F7',
  red:    '#9E2B25',
  orange: '#F29E4C',
  green:  '#2CA87F',
}

const thStyle = {
  fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase',
  letterSpacing: '0.05em', padding: '8px 12px', background: '#f8f9fb',
  borderBottom: '1px solid #e8ecf0', whiteSpace: 'nowrap',
}
const tdStyle = {
  fontSize: 13, color: '#2d3748', padding: '8px 12px',
  verticalAlign: 'middle', borderBottom: '1px solid #f0f2f5',
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  try {
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch { return 'N/A' }
}

const statusStyle = (status) => {
  if ((status || '').toLowerCase() === 'completed')
    return { bg: `${COLORS.green}18`, color: COLORS.green }
  if ((status || '').toLowerCase() === 'cancelled')
    return { bg: `${COLORS.red}15`, color: COLORS.red }
  return { bg: `${COLORS.blue}18`, color: COLORS.blue }
}

const GenderBadge = ({ gender }) => {
  const g = (gender || '').toLowerCase()
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
      background: g === 'male' ? '#d0e8ff' : g === 'female' ? '#fce4ec' : '#eee',
      color: g === 'male' ? '#1565c0' : g === 'female' ? '#880e4f' : '#555',
    }}>
      {gender || '—'}
    </span>
  )
}

// ─── Print helper ─────────────────────────────────────────────────────────────

const printGroup = (group, patientInfo) => {
  const businessSetting = JSON.parse(localStorage.getItem('businessSetting') || '{}')
  const hospitalName  = businessSetting?.official_name || 'N/A'
  const address       = businessSetting?.address || 'N/A'
  const crNumber      = businessSetting?.cr_number || 'N/A'
  const vatNumber     = businessSetting?.vat_number || ''
  const phone         = businessSetting?.telephone_1 || 'N/A'
  const email         = businessSetting?.email || 'N/A'
  const website       = businessSetting?.website || ''
  const defaultLogo   = new URL(logo1, window.location.origin).href
  const logoSrc       = businessSetting?.image ? `${baseurImage}${businessSetting.image}` : defaultLogo
  const first         = group[0]
  const fileName      = `Prescription_${patientInfo?.name || 'Patient'}`

  const rows = group.map((rx, i) => `
    <tr>
      <td>${i + 1}</td>
      <td><strong>${rx.medicine_name || rx.medicineName || '—'}</strong>${rx.strength ? ` — ${rx.strength}` : ''}${rx.dose ? ` — ${rx.dose}` : ''}</td>
      <td>${rx.route || '—'}</td>
      <td>${rx.frequency || '—'}</td>
      <td>${rx.duration || '—'}</td>
      <td>${rx.substance || '—'}</td>
      <td>${rx.notes || '—'}</td>
    </tr>`).join('')

  const win = window.open('', '_blank')
  win.document.open()
  win.document.write(`
<html>
<head>
  <title>${fileName}</title>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.3; color: #222; }
    .wrap { width: 210mm; margin: 0 auto; }
    @page { size: A4; margin: 10mm; }
    table { width: 100%; border-collapse: collapse; margin-top: 16px; }
    th, td { border: 1px solid #999; padding: 5px 8px; font-size: 12px; text-align: left; }
    th { background: #f5f5f5; font-weight: 700; text-transform: uppercase; font-size: 10px; }
    .header { text-align: center; margin-bottom: 24px; }
    .section { display: flex; justify-content: space-between; border-top: 1px solid #000; padding-top: 12px; margin-top: 12px; }
    .rx { font-size: 48px; font-weight: 700; border-top: 1px solid #000; padding-top: 12px; margin-top: 16px; }
    .sig { margin-top: 60px; padding-bottom: 60px; border-bottom: 1px solid #000; width: 220px; font-size: 13px; }
    .footer { text-align: center; font-size: 11px; margin-top: 40px; color: #555; }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="header">
      <img src="${logoSrc}" onerror="this.src='${defaultLogo}'" alt="Logo" style="height:70px" />
      <h2 style="margin:16px 0 4px">${hospitalName}</h2>
      <p style="margin:0">Address: ${address}</p>
      <p style="margin:0">CR: ${crNumber} &nbsp;|&nbsp; VAT: ${vatNumber}</p>
    </div>
    <div class="section">
      <div>
        <p><strong>Patient:</strong> ${patientInfo?.name || 'N/A'}</p>
        <p><strong>Gender:</strong> ${patientInfo?.gender || 'N/A'}</p>
        <p><strong>Age:</strong> ${patientInfo?.age || 'N/A'}</p>
        <p><strong>Civil ID:</strong> ${patientInfo?.civilId || 'N/A'}</p>
        <p><strong>Phone:</strong> ${patientInfo?.phone || 'N/A'}</p>
      </div>
      <div>
        <p><strong>Doctor:</strong> ${first.doctorName || 'N/A'}</p>
        <p><strong>License:</strong> ${first.doctorLicenseId || 'N/A'}</p>
        <p><strong>Date:</strong> ${formatDate(first.created_at)}</p>
        <p><strong>Ref #:</strong> ${first.prescription_group_id || first.id}</p>
      </div>
    </div>
    <div class="rx">Rx</div>
    <table>
      <thead>
        <tr>
          <th>#</th><th>Medicine</th><th>Route</th><th>Frequency</th>
          <th>Duration</th><th>Substance</th><th>Notes</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="sig"><strong>Signature &amp; Stamp:</strong></div>
    <div class="footer">
      ${hospitalName} | ${email} | ${phone}${website ? ' | ' + website : ''}
    </div>
  </div>
  <script>window.onload = function(){ window.focus(); window.print(); }</script>
</body>
</html>`)
  win.document.close()
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ViewPrescription() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [prescriptions, setPrescriptions] = useState([])
  const [patientInfo, setPatientInfo] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchPrescriptions() }, [id])

  const fetchPrescriptions = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.get('getAllPrescriptions')
      if (response.data?.success) {
        const list = response.data.data.filter(p => p.patient_id === parseInt(id))
        setPrescriptions(list)
        if (list.length > 0) {
          const f = list[0]
          setPatientInfo({
            id:      f.patient_id,
            name:    f.fullName,
            gender:  f.gender,
            age:     f.age,
            civilId: f.civilIdNumber,
            phone:   f.mobileNumber,
          })
        } else {
          setPatientInfo({})
        }
      }
    } catch (error) {
      console.error('Error fetching prescriptions:', error)
      Swal.fire('Error', 'Failed to fetch prescriptions', 'error')
    } finally {
      setLoading(false)
    }
  }

  // ── Derived data ──────────────────────────────────────────────────────────

  const grouped = prescriptions.reduce((acc, rx) => {
    const key = rx.prescription_group_id ? String(rx.prescription_group_id) : `single_${rx.id}`
    if (!acc[key]) acc[key] = []
    acc[key].push(rx)
    return acc
  }, {})

  const groups = Object.values(grouped).sort(
    (a, b) => new Date(b[0].created_at || 0) - new Date(a[0].created_at || 0)
  )

  const uniqueDoctors = [...new Set(prescriptions.map(p => p.doctorName).filter(Boolean))]
  const lastDate = prescriptions.length > 0
    ? formatDate(Math.max(...prescriptions.map(p => new Date(p.created_at || 0))))
    : 'N/A'

  // ── Loading ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="pc-container">
        <div className="pc-content">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <div className="spinner-border" role="status" style={{ color: COLORS.blue }}>
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ── Not found ─────────────────────────────────────────────────────────────

  if (!patientInfo || !patientInfo.name) {
    return (
      <div className="pc-container">
        <div className="pc-content">
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', padding: '56px 24px', textAlign: 'center' }}>
            <i className="ti ti-clipboard-off" style={{ fontSize: 52, color: '#dde3eb', display: 'block', marginBottom: 16 }} />
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1a202c', marginBottom: 6 }}>No prescriptions found</div>
            <div style={{ fontSize: 13, color: COLORS.gray, marginBottom: 24 }}>This patient doesn't have any prescriptions on record.</div>
            <button
              onClick={() => navigate(-1)}
              className="bgBtn btn-sm d-flex align-items-center gap-1"
              style={{ fontSize: 13, padding: '7px 18px', margin: '0 auto' }}
            >
              <i className="ti ti-arrow-left" style={{ fontSize: 14 }} /> Go Back
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>

          {/* ── Page Header ── */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => navigate(-1)}
              title="Go back"
              style={{
                width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0',
                background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, marginRight: 4, flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '22'; e.currentTarget.style.color = COLORS.blue }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = COLORS.gray }}
            >
              <i className="ti ti-arrow-left" />
            </button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.blue}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="ti ti-file-description" style={{ color: COLORS.blue, fontSize: 17 }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Patient Prescriptions</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>
                Medication history for <strong style={{ color: '#2d3748' }}>{patientInfo.name}</strong>
              </div>
            </div>
          </div>

          {/* ── Patient Info + Stats Bar ── */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', background: '#fafbfc' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>

              {/* Patient Identity Card */}
              <div style={{
                background: '#fff', border: '1px solid #e8ecf0', borderRadius: 10,
                padding: '12px 16px', flex: '1 1 240px',
                display: 'flex', alignItems: 'center', gap: 14,
                borderLeft: `4px solid ${COLORS.blue}`,
              }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: `${COLORS.blue}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="ti ti-user" style={{ color: COLORS.blue, fontSize: 22 }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: COLORS.gray, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Patient</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#1a202c', marginBottom: 4 }}>{patientInfo.name}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {patientInfo.gender && <GenderBadge gender={patientInfo.gender} />}
                    {patientInfo.age && (
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: `${COLORS.gray}15`, color: COLORS.gray }}>
                        Age {patientInfo.age}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Patient Contact Details */}
              <div style={{ background: '#fff', border: '1px solid #e8ecf0', borderRadius: 10, padding: '12px 16px', flex: '1 1 200px' }}>
                <div style={{ fontSize: 11, color: COLORS.gray, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                  <i className="ti ti-id-badge" style={{ marginRight: 4 }} />Identification
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {patientInfo.civilId && (
                    <div style={{ fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Civil ID: </span>
                      <span style={{ fontWeight: 600, color: '#2d3748' }}>{patientInfo.civilId}</span>
                    </div>
                  )}
                  {patientInfo.phone && (
                    <div style={{ fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Phone: </span>
                      <span style={{ fontWeight: 600, color: '#2d3748' }}>{patientInfo.phone}</span>
                    </div>
                  )}
                  {!patientInfo.civilId && !patientInfo.phone && (
                    <div style={{ fontSize: 12, color: COLORS.gray }}>No details on record</div>
                  )}
                </div>
              </div>

              {/* Stat Cards */}
              {[
                { label: 'Rx Sessions',      value: groups.length,           icon: 'ti-clipboard-list', color: COLORS.blue   },
                { label: 'Total Medicines',  value: prescriptions.length,    icon: 'ti-pill',           color: COLORS.green  },
                { label: 'Doctors Involved', value: uniqueDoctors.length,    icon: 'ti-stethoscope',    color: COLORS.orange },
                { label: 'Last Prescribed',  value: lastDate,                icon: 'ti-calendar',       color: COLORS.gray   },
              ].map(stat => (
                <div key={stat.label} style={{
                  background: '#fff', border: '1px solid #e8ecf0', borderRadius: 10,
                  padding: '12px 16px', flex: '1 1 130px', display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: `${stat.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className={`ti ${stat.icon}`} style={{ color: stat.color, fontSize: 18 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: COLORS.gray, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{stat.label}</div>
                    <div style={{ fontSize: stat.label === 'Last Prescribed' ? 12 : 18, fontWeight: 700, color: '#1a202c', lineHeight: 1.2 }}>{stat.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Doctors involved chips */}
            {uniqueDoctors.length > 0 && (
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: COLORS.gray, fontWeight: 600 }}>
                  <i className="ti ti-stethoscope" style={{ marginRight: 4 }} />Prescribing Doctors:
                </span>
                {uniqueDoctors.map(doc => (
                  <span key={doc} style={{ fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 10, background: `${COLORS.green}15`, color: COLORS.green }}>
                    {doc}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* ── Prescription Groups ── */}
          <div style={{ padding: '16px 20px' }}>

            {groups.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '56px 24px' }}>
                <i className="ti ti-database-off" style={{ fontSize: 44, display: 'block', marginBottom: 14, color: '#dde3eb' }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.gray, marginBottom: 4 }}>No prescriptions found</div>
                <div style={{ fontSize: 12, color: COLORS.gray }}>This patient doesn't have any prescriptions yet.</div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, marginBottom: 12 }}>
                  {groups.length} Prescription Session{groups.length !== 1 ? 's' : ''} — sorted newest first
                </div>

                {groups.map((group, gi) => {
                  const first       = group[0]
                  const groupStatus = first.status || 'Plan'
                  const sc          = statusStyle(groupStatus)
                  const accent      = groupStatus.toLowerCase() === 'completed' ? COLORS.green : COLORS.blue
                  const groupId     = first.prescription_group_id || first.id
                  const groupDate   = formatDate(first.created_at)

                  return (
                    <div
                      key={gi}
                      style={{
                        borderRadius: 10, border: '1px solid #e8ecf0',
                        marginBottom: 14, overflow: 'hidden',
                        borderLeft: `4px solid ${accent}`,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                      }}
                    >
                      {/* Group Header */}
                      <div style={{
                        padding: '10px 16px', background: '#fafbfc',
                        borderBottom: '1px solid #e8ecf0',
                        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
                      }}>
                        {/* Icon + Ref */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 28, height: 28, borderRadius: 7, background: `${accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="ti ti-file-text" style={{ color: accent, fontSize: 14 }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c' }}>
                              Rx #{groupId}
                            </div>
                            <div style={{ fontSize: 11, color: COLORS.gray }}>
                              {groupDate} &middot; {group.length} medicine{group.length !== 1 ? 's' : ''}
                            </div>
                          </div>
                        </div>

                        {/* Divider */}
                        <div style={{ width: 1, height: 28, background: '#e8ecf0' }} />

                        {/* Doctor */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ width: 24, height: 24, borderRadius: 6, background: `${COLORS.green}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="ti ti-stethoscope" style={{ color: COLORS.green, fontSize: 13 }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#2d3748' }}>{first.doctorName || 'N/A'}</div>
                            {first.doctorLicenseId && (
                              <div style={{ fontSize: 10, color: COLORS.gray }}>Lic: {first.doctorLicenseId}</div>
                            )}
                          </div>
                        </div>

                        {/* Status + Print – pushed to the right */}
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: sc.bg, color: sc.color, whiteSpace: 'nowrap' }}>
                            {groupStatus}
                          </span>
                          <button
                            onClick={() => printGroup(group, patientInfo)}
                            title="Print this prescription"
                            style={{
                              width: 30, height: 30, borderRadius: 8, border: 'none', cursor: 'pointer',
                              background: `${COLORS.gray}15`, color: COLORS.gray,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
                              transition: 'background 0.2s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = COLORS.gray + '30'; e.currentTarget.style.color = '#2d3748' }}
                            onMouseLeave={e => { e.currentTarget.style.background = COLORS.gray + '15'; e.currentTarget.style.color = COLORS.gray }}
                          >
                            <i className="ti ti-printer" />
                          </button>
                        </div>
                      </div>

                      {/* Medicines Table */}
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={{ ...thStyle, width: 36 }}>#</th>
                              <th style={{ ...thStyle, minWidth: 160 }}>Medicine</th>
                              <th style={thStyle}>Strength</th>
                              <th style={thStyle}>Dose</th>
                              <th style={thStyle}>Frequency</th>
                              <th style={thStyle}>Duration</th>
                              <th style={thStyle}>Route</th>
                              <th style={{ ...thStyle, minWidth: 130 }}>Substance</th>
                              <th style={{ ...thStyle, minWidth: 160 }}>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.map((rx, ri) => (
                              <tr
                                key={rx.id}
                                style={{ background: ri % 2 === 0 ? '#fff' : '#fafbfc' }}
                                onMouseEnter={e => e.currentTarget.style.background = `${accent}0d`}
                                onMouseLeave={e => e.currentTarget.style.background = ri % 2 === 0 ? '#fff' : '#fafbfc'}
                              >
                                <td style={{ ...tdStyle, color: COLORS.gray, fontWeight: 600, fontSize: 11 }}>{ri + 1}</td>

                                {/* Medicine name — most important field */}
                                <td style={tdStyle}>
                                  <div style={{ fontWeight: 700, fontSize: 13, color: '#1a202c' }}>
                                    {rx.medicine_name || rx.medicineName || '—'}
                                  </div>
                                  {rx.packet_size && (
                                    <div style={{ fontSize: 11, color: COLORS.gray, marginTop: 1 }}>Pack: {rx.packet_size}</div>
                                  )}
                                </td>

                                {/* Strength as a badge */}
                                <td style={tdStyle}>
                                  {rx.strength
                                    ? <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: `${accent}18`, color: accent, whiteSpace: 'nowrap' }}>
                                        {rx.strength}
                                      </span>
                                    : <span style={{ color: COLORS.gray }}>—</span>}
                                </td>

                                <td style={tdStyle}>{rx.dose || '—'}</td>
                                <td style={tdStyle}>
                                  {rx.frequency
                                    ? <span style={{ fontSize: 12, fontWeight: 600, color: '#2d3748' }}>{rx.frequency}</span>
                                    : '—'}
                                </td>
                                <td style={tdStyle}>{rx.duration || '—'}</td>
                                <td style={tdStyle}>{rx.route || '—'}</td>

                                <td style={{ ...tdStyle, maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={rx.substance}>
                                  {rx.substance || '—'}
                                </td>

                                <td style={{ ...tdStyle, maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={rx.notes}>
                                  {rx.notes
                                    ? <span style={{ fontSize: 12, color: COLORS.gray, fontStyle: 'italic' }}>{rx.notes}</span>
                                    : <span style={{ color: COLORS.gray }}>—</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                    </div>
                  )
                })}
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}
